import { Mentor } from '../../../mocks/mentors';
import { sanitizeRichHtml } from '@/lib/richText';

interface Props {
  mentor: Mentor;
}

interface TimelineEntry {
  period: string;
  role: string;
  company: string;
  desc: string;
  descHtml?: string;
  icon: string;
  color: string;
  bg: string;
  iconColor: string;
  current?: boolean;
}

const buildTimeline = (mentor: Mentor): TimelineEntry[] => {
  const richBio = sanitizeRichHtml(mentor.bio);

  const entries: TimelineEntry[] = [
    {
      period: '專業基礎',
      role: `${mentor.category} 領域專家`,
      company: '',
      desc: '',
      descHtml: richBio,
      icon: 'ri-graduation-cap-line',
      color: '#7C3AED',
      bg: 'bg-violet-50',
      iconColor: 'text-violet-600',
    },
    {
      period: '職業積累',
      role: '資深產業專家',
      company: mentor.company,
      desc: sentences[1] ?? `在 ${mentor.company} 積累了深厚的產業經驗，專注於 ${mentor.expertise.slice(0, 2).join('與')} 等核心方向。`,
      icon: 'ri-briefcase-4-line',
      color: '#4285F4',
      bg: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
  ];

  entries.push({
    period: '導師成就',
    role: '職涯導師',
    company: 'NexusGo',
    desc: `已協助 ${mentor.sessions}+ 位學員成功進入頂尖外商，持續陪伴學員從投遞到 Offer 全過程。`,
    icon: 'ri-team-line',
    color: '#F59E0B',
    bg: 'bg-amber-50',
    iconColor: 'text-amber-600',
  });

  entries.push({
    period: '當前職位',
    role: mentor.title,
    company: mentor.company,
    desc: `專注於${mentor.expertise.slice(0, 3).join('、')}等方向，持續協助 AI 從業者實現全球職涯突破。`,
    icon: 'ri-cpu-line',
    color: mentor.companyColor,
    bg: 'bg-slate-50',
    iconColor: 'text-slate-600',
    current: true,
  });

  return entries;
};

const CareerTimeline = ({ mentor }: Props) => {
  const timeline = buildTimeline(mentor);

  return (
    <section className="bg-white rounded-2xl border border-slate-100 p-7">
      <h3 className="font-bold text-navy-800 text-base mb-6 flex items-center gap-2">
        <i className="ri-time-line text-violet-500" />
        職業發展軌跡
      </h3>

      <div className="relative">
        <div className="absolute left-5 top-2 bottom-2 w-px bg-gradient-to-b from-violet-200 via-blue-200 to-green-200" />

        <div className="flex flex-col gap-6">
          {timeline.map((item, i) => (
            <div key={i} className="relative flex gap-5">
              <div
                className={`relative z-10 w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl ${item.bg} border border-slate-100`}
              >
                <i className={`${item.icon} ${item.iconColor} text-base`} />
              </div>

              <div className="flex-1 pb-2">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-navy-800 text-sm">{item.role}</span>
                      {item.company && (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{ color: item.color, backgroundColor: item.color + '15' }}
                        >
                          @ {item.company}
                        </span>
                      )}
                      {item.current && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                          當前職位
                        </span>
                      )}
                    </div>
                    <div className="text-violet-500 text-xs font-medium mt-0.5">{item.period}</div>
                  </div>
                </div>
                {item.descHtml ? (
                  <div
                    className="mentor-rich-content prose-custom mt-2 text-slate-500 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: item.descHtml }}
                  />
                ) : (
                  <p className="text-slate-500 text-sm leading-relaxed mt-2">{item.desc}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CareerTimeline;
